/**
 * Gallery — the open-at hook (toolkit#798 / 3.275.0 parity: the toolkit
 * exposes `window.hrtkPhotoGallery.openAt({ src, room, property })` and the
 * Bedroom Showcase hands a tapped photo to it). Here the hook is a cancelable
 * CustomEvent on `window`, the way the booking and calendar widgets already
 * talk across bundles:
 *
 *  - a caller dispatches `homerunner:gallery-open-at` with a
 *    GalleryOpenAtRequest through requestGalleryOpenAt();
 *  - a Gallery widget that owns the property (galleryOwnsRequest) and finds
 *    the target (resolveOpenAtIndex) calls preventDefault() and opens its
 *    "Show all photos" browser there;
 *  - requestGalleryOpenAt() returns true exactly when a gallery took it, so
 *    the caller can fall back to its own viewer otherwise.
 *
 * Resolution, as in the toolkit: the photo itself first (by URL, the same
 * string the API handed both widgets), else the room by uid, else the room
 * by name when exactly one room carries it. The resolver is pure.
 */
import type { PropertyImage } from './data'
import { normalizeRoomText, type TourSection } from './rooms'

export const GALLERY_OPEN_AT_EVENT = 'homerunner:gallery-open-at'

export type GalleryOpenAtRequest = {
  /** The caller's feed id; a gallery on another feed refuses the request. */
  feedId?: number
  /** The property slug; required, a gallery for another property refuses. */
  property: string
  /** The tapped photo's URL as the API sent it. */
  url?: string
  /** The PMS room uid the photo (or its card) belongs to. */
  roomUid?: string
  /** The room name as the caller shows it; the last resort. */
  roomName?: string
}

/** Whether a gallery with this filter should answer the request. */
export function galleryOwnsRequest(
  filter: { feed_id?: number | null; property?: string | null },
  request: GalleryOpenAtRequest | null | undefined,
): boolean {
  if (!request || typeof request !== 'object') return false
  const property = typeof request.property === 'string' ? request.property : ''
  if (property === '' || property !== (filter.property ?? '')) return false
  const want = request.feedId
  const own = filter.feed_id
  if (typeof want === 'number' && typeof own === 'number' && want !== own) return false
  return true
}

/**
 * The GALLERY index to open at, or undefined when nothing matches. The
 * fullscreen browser maps a gallery index to its tour position itself.
 */
export function resolveOpenAtIndex(
  images: ReadonlyArray<PropertyImage>,
  tour: ReadonlyArray<TourSection<PropertyImage>> | null,
  request: GalleryOpenAtRequest,
): number | undefined {
  const url = typeof request.url === 'string' ? request.url : ''
  if (url !== '') {
    const byUrl = images.findIndex((photo) => photo?.url === url)
    if (byUrl >= 0) return byUrl
  }

  const firstIndexOf = (photos: ReadonlyArray<PropertyImage>): number | undefined => {
    const index = photos.length > 0 ? images.indexOf(photos[0]) : -1
    return index >= 0 ? index : undefined
  }

  const uid = typeof request.roomUid === 'string' ? request.roomUid.trim() : ''
  if (uid !== '') {
    if (tour) {
      // A renamed or merged room keeps its photos' uid though its key changed.
      const section = tour.find((s) => s.key === uid || s.photos.some((p) => (p.room_uid ?? '') === uid))
      if (section) return firstIndexOf(section.photos)
    } else {
      const index = images.findIndex((photo) => (photo?.room_uid ?? '') === uid)
      if (index >= 0) return index
    }
  }

  const name = normalizeRoomText(typeof request.roomName === 'string' ? request.roomName : '')
  if (name === '') return undefined
  if (tour) {
    const hits = tour.filter((s) => normalizeRoomText(s.title) === name)
    return hits.length === 1 ? firstIndexOf(hits[0].photos) : undefined
  }
  const uids = new Set<string>()
  let first = -1
  images.forEach((photo, index) => {
    const photoUid = typeof photo?.room_uid === 'string' ? photo.room_uid.trim() : ''
    if (photoUid === '' || normalizeRoomText(typeof photo.room_name === 'string' ? photo.room_name : '') !== name) return
    if (!uids.has(photoUid)) {
      uids.add(photoUid)
      if (first < 0) first = index
    }
  })
  return uids.size === 1 ? first : undefined
}

/** Ask the galleries on the page to open at a photo or room. True when one did. */
export function requestGalleryOpenAt(request: GalleryOpenAtRequest): boolean {
  if (typeof window === 'undefined' || typeof CustomEvent !== 'function') return false
  const event = new CustomEvent<GalleryOpenAtRequest>(GALLERY_OPEN_AT_EVENT, { detail: request, cancelable: true })
  // dispatchEvent is false once a listener called preventDefault().
  return !window.dispatchEvent(event)
}
